"use client";
import { useCallback, useMemo, useState } from "react";
import { calendarDateInTimeZone } from "@utils/core";
import { dateFromDay, dayFromDate } from "./calendarDay";

/**
 * The day a booking date picker has selected. It starts on the location's
 * today in `timeZone`, not the visitor's (BF-DEC-015): the openings API reads
 * `date` as that location's calendar day, so a visitor a day away from the
 * business would otherwise open on the business's yesterday or tomorrow.
 * `initialDay` (`YYYY-MM-DD`) starts it on a fixed day instead.
 *
 * - Until the customer picks a day, the start follows `timeZone`, so a
 *   location chosen after mount opens on its own today.
 * - A picked day is kept across a change of location unless it comes before
 *   the new location's today; then the selection moves up to that today, so
 *   the openings API is never asked for a day the location has left behind.
 * - The start is read from the clock once per zone, so it stays put while
 *   the page is open. The picker's earliest day still moves on at the
 *   location's midnight (`useLocationDay`).
 * - Without a zone it is the runtime's own day: the browser's once hydrated.
 *
 * Nothing may render this day as text before hydration; `DateSelector`
 * waits for `useLocationDay` (#553).
 */
export function useBookingDate(
  timeZone: string | null | undefined,
  initialDay?: string | null,
): [Date, (date: Date) => void] {
  const zone = timeZone || null;
  const [fixedDay] = useState<string | null>(initialDay ?? null);
  const [pickedDay, setPickedDay] = useState<string | null>(null);
  const zoneToday = useMemo(
    () => calendarDateInTimeZone(new Date(), zone),
    [zone],
  );
  // `YYYY-MM-DD` days compare in calendar order as strings.
  const day =
    pickedDay === null
      ? (fixedDay ?? zoneToday)
      : pickedDay < zoneToday
        ? zoneToday
        : pickedDay;
  const date = useMemo(() => dateFromDay(day), [day]);
  const setDate = useCallback(
    (next: Date) => setPickedDay(dayFromDate(next)),
    [],
  );
  return [date, setDate];
}
